/**
 * Work assignment, general test execution and the minimal equipment register —
 * modules C5 and C6, Phase 8.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 4 BEFORE ADDING ANYTHING HERE.
 * ===========================================================================
 *
 * THIS IS THE GENERAL PATH, AND IT IS DELIBERATELY NON-SCIENTIFIC. A general
 * result is a person's written summary of work they performed, the notes they
 * kept, the equipment they used, the dates they worked and the files they
 * attached. There is no input field, reading, unit, formula, calculation
 * order, rounding rule, acceptance limit, pass/fail rule, chart, axis or Excel
 * cell mapping anywhere in this file — for any test, including the five
 * initial ones — and `GENERAL_RESULT_FIELDS` below is the complete list of
 * what a general result carries.
 *
 * Five ideas govern these shapes, each an acceptance criterion rather than a
 * preference:
 *
 *  1. ONLY AN ACCEPTED SAMPLE RECEIVES WORK. The rule belongs to the sample
 *     and lives in `sampleMayBeAssignedTests`; nothing here restates it.
 *
 *  2. AN ATTEMPT BELONGS TO ITS PERFORMER FOREVER (D89). Reassigning the work
 *     never reattributes what somebody already entered: the earlier attempt
 *     keeps its performer and is closed as `discontinued`, and the new
 *     assignee starts an attempt of their own.
 *
 *  3. A RETEST IS A NEW ATTEMPT WITH A REASON, never an edit of a submitted
 *     one and never an unexplained duplicate.
 *
 *  4. SUBMITTING IS NOT APPROVAL. The furthest the performer can take their
 *     own work is `awaiting_review`. Review and approval are Phase 9, separate
 *     permissions, and never available on one's own result.
 *
 *  5. EQUIPMENT IS BLOCKED BY ITS OWN STATE AND ITS OWN CALIBRATION DATE, and
 *     by nothing about the test (D85). Which apparatus a test requires is not
 *     known and is not invented here (limitation 26).
 */
import { TEST_STATUS } from './status.js';
// ---------------------------------------------------------------------------
// The state of an assignment
// ---------------------------------------------------------------------------
/**
 * The test-execution statuses an assignment can hold.
 *
 * A projection of `TEST_STATUS`, which is the canonical model. Phase 9 added
 * the review and approval states — returned for correction, retest required,
 * awaiting approval and approved — and each is reached ONLY through the
 * review or approval actions of somebody who is not the performer.
 */
export const ASSIGNMENT_STATUS = {
    NOT_STARTED: TEST_STATUS.NOT_STARTED,
    IN_PROGRESS: TEST_STATUS.IN_PROGRESS,
    /** Paused for a recorded reason. Work resumes; nothing is cancelled. */
    TEMPORARILY_STOPPED: TEST_STATUS.TEMPORARILY_STOPPED,
    /** Submitted by its performer. NOT approved, and not reviewed. */
    AWAITING_REVIEW: TEST_STATUS.AWAITING_REVIEW,
    /** A reviewer or an approver sent it back, with a reason (Phase 9). */
    RETURNED_FOR_CORRECTION: TEST_STATUS.RETURNED_FOR_CORRECTION,
    /** A reviewer required the work itself to be performed again (Phase 9). */
    RETEST_REQUIRED: TEST_STATUS.RETEST_REQUIRED,
    /** Technically accepted. Awaiting the SEPARATE approval act (Phase 9). */
    AWAITING_APPROVAL: TEST_STATUS.AWAITING_APPROVAL,
    /** Approved and LOCKED. A change now is a recorded correction (Phase 9). */
    APPROVED: TEST_STATUS.APPROVED,
    CANCELLED: TEST_STATUS.CANCELLED,
};
export const ALL_ASSIGNMENT_STATUSES = Object.freeze(Object.values(ASSIGNMENT_STATUS));
/**
 * Work somebody in the laboratory still has to do.
 *
 * Returned work and work a reviewer sent back for a retest are OPEN: somebody
 * has to act on them, they can run late, and they can be reassigned, stopped
 * or cancelled like any other unfinished work. Work waiting for a reviewer or
 * an approver is NOT open — the person doing it has done their part — and
 * approved work is finished.
 */
export const OPEN_ASSIGNMENT_STATUSES = Object.freeze([
    ASSIGNMENT_STATUS.NOT_STARTED,
    ASSIGNMENT_STATUS.IN_PROGRESS,
    ASSIGNMENT_STATUS.TEMPORARILY_STOPPED,
    ASSIGNMENT_STATUS.RETURNED_FOR_CORRECTION,
    ASSIGNMENT_STATUS.RETEST_REQUIRED,
]);
export function assignmentIsOpen(status) {
    return OPEN_ASSIGNMENT_STATUSES.includes(status);
}
/**
 * The follow-up lists of the laboratory work list.
 *
 * `delayed` is not a status: it is a question about dates and recorded
 * delays, answered by `assignmentIsDelayed`, so a delayed assignment is still
 * `not_started` or `in_progress` — which is the truth about it.
 */
export const ASSIGNMENT_LIST_VIEW = {
    ALL: 'all',
    UNASSIGNED: 'unassigned',
    IN_PROGRESS: 'in_progress',
    DELAYED: 'delayed',
    STOPPED: 'stopped',
    AWAITING_REVIEW: 'awaiting_review',
    RETURNED: 'returned',
    RETEST_REQUIRED: 'retest_required',
    AWAITING_APPROVAL: 'awaiting_approval',
    APPROVED: 'approved',
    CANCELLED: 'cancelled',
};
export const ALL_ASSIGNMENT_LIST_VIEWS = Object.freeze(Object.values(ASSIGNMENT_LIST_VIEW));
/**
 * Is this assignment delayed, as of `today`?
 *
 * Delayed means OPEN and EITHER a person recorded a delay OR the date the
 * laboratory is working to has passed. The date worked to is the latest
 * recorded expected date, else the original due date. An assignment with
 * neither date and no recorded delay is never delayed: nobody promised a date,
 * so nothing can be late against one (`CLAUDE.md` section 8).
 *
 * `today` is a calendar date (YYYY-MM-DD) in the laboratory's own timezone,
 * taken from ONE clock by the caller — the same lesson as decision D81.
 */
export function assignmentIsDelayed(assignment, today) {
    if (!assignmentIsOpen(assignment.status))
        return false;
    if (assignment.hasRecordedDelay)
        return true;
    const workingTo = assignment.expectedDate ?? assignment.dueDate;
    return workingTo !== null && workingTo < today;
}
// ---------------------------------------------------------------------------
// The history of an assignment — append-only
// ---------------------------------------------------------------------------
export const ASSIGNMENT_EVENT_TYPE = {
    CREATED: 'created',
    /** A person was given work nobody held. */
    ASSIGNED: 'assigned',
    /** The work moved from one person to another. Requires a reason. */
    REASSIGNED: 'reassigned',
    DEPARTMENT_TRANSFERRED: 'department_transferred',
    DETAILS_UPDATED: 'details_updated',
    /** A delay with its reason and the new expected date. */
    DELAY_RECORDED: 'delay_recorded',
    /** A temporary stop with its reason. */
    STOPPED: 'stopped',
    RESUMED: 'resumed',
    ATTEMPT_STARTED: 'attempt_started',
    /** A draft closed without submission — its content and performer kept. */
    ATTEMPT_DISCONTINUED: 'attempt_discontinued',
    /** Submitted for review. Not approval. */
    SUBMITTED: 'submitted',
    // --- Phase 9. Each is somebody else's recorded act on somebody's work. ---
    /** A reviewer accepted the record technically. NOT an approval. */
    REVIEW_ACCEPTED: 'review_accepted',
    /** A reviewer sent it back to its performer, with a reason. */
    REVIEW_RETURNED: 'review_returned',
    /** A reviewer required the work to be performed again, with a reason. */
    RETEST_REQUIRED: 'retest_required',
    /** Approved. This says the RECORD was checked, not that the material
     *  passed (`CLAUDE.md` section 8). */
    APPROVED: 'approved',
    /** An approver sent it back to its performer, with a reason. */
    APPROVAL_RETURNED: 'approval_returned',
    /** A post-approval correction procedure was opened, with a reason. */
    CORRECTION_OPENED: 'correction_opened',
    /** The corrected result completed review and approval again. */
    CORRECTION_RESOLVED: 'correction_resolved',
    CANCELLED: 'cancelled',
};
export const ALL_ASSIGNMENT_EVENT_TYPES = Object.freeze(Object.values(ASSIGNMENT_EVENT_TYPE));
/**
 * Events that must say why. A delay, a stop, a cancellation or taking work
 * away from somebody with no stated cause is not something anyone can account
 * for later.
 */
export const ASSIGNMENT_EVENTS_REQUIRING_REASON = Object.freeze([
    ASSIGNMENT_EVENT_TYPE.REASSIGNED,
    ASSIGNMENT_EVENT_TYPE.DELAY_RECORDED,
    ASSIGNMENT_EVENT_TYPE.STOPPED,
    ASSIGNMENT_EVENT_TYPE.ATTEMPT_DISCONTINUED,
    ASSIGNMENT_EVENT_TYPE.CANCELLED,
    // Phase 9: sending work back, requiring it again, or reopening an
    // approved result are never recorded without a stated reason.
    ASSIGNMENT_EVENT_TYPE.REVIEW_RETURNED,
    ASSIGNMENT_EVENT_TYPE.RETEST_REQUIRED,
    ASSIGNMENT_EVENT_TYPE.APPROVAL_RETURNED,
    ASSIGNMENT_EVENT_TYPE.CORRECTION_OPENED,
]);
export function assignmentEventRequiresReason(eventType) {
    return ASSIGNMENT_EVENTS_REQUIRING_REASON.includes(eventType);
}
// ---------------------------------------------------------------------------
// Attempts
// ---------------------------------------------------------------------------
export const ATTEMPT_STATUS = {
    /** Saved and reloadable. Not submitted, not a result anybody relies on. */
    DRAFT: 'draft',
    /** Submitted for review by its performer, and locked. */
    SUBMITTED: 'submitted',
    /**
     * Sent back by a reviewer or an approver, with a reason. Open again for its
     * PERFORMER — and for nobody else — to correct. What was submitted is not
     * lost: every submission is a frozen revision (Phase 9, D99).
     */
    RETURNED: 'returned',
    /** Technically accepted by review, awaiting the SEPARATE approval act. */
    TECHNICALLY_ACCEPTED: 'technically_accepted',
    /** Approved and LOCKED. A change now is a recorded correction (D102). */
    APPROVED: 'approved',
    /** A reviewer required the work to be performed again. This attempt is
     *  closed and kept; the retest is a NEW attempt (D98). */
    RETEST_REQUIRED: 'retest_required',
    /** Closed without submission, e.g. when the work was reassigned. Its
     *  content and its performer are kept exactly as they were. */
    DISCONTINUED: 'discontinued',
};
export const ALL_ATTEMPT_STATUSES = Object.freeze(Object.values(ATTEMPT_STATUS));
/**
 * The states in which the PERFORMER, and only the performer, may write the
 * content of an attempt.
 *
 * Every other state is closed to editing — by the API and by database trigger.
 * `approved` is closed by an active lock row as well, which is what "locked in
 * the database" means (D101).
 */
export const EDITABLE_ATTEMPT_STATUSES = Object.freeze([ATTEMPT_STATUS.DRAFT, ATTEMPT_STATUS.RETURNED]);
export function attemptContentIsEditable(status) {
    return EDITABLE_ATTEMPT_STATUSES.includes(status);
}
/**
 * States in which the work is somebody else's to act on: a reviewer's, an
 * approver's, or nobody's because it is already approved.
 *
 * No new attempt may begin on an assignment while one of these is live — which
 * is how a performer is prevented from retesting their own submitted work
 * before anybody has looked at it (limitation 40, closed in Phase 9 by D105).
 */
export const CONCLUDED_ATTEMPT_STATUSES = Object.freeze([
    ATTEMPT_STATUS.RETEST_REQUIRED,
    ATTEMPT_STATUS.DISCONTINUED,
]);
export function attemptBlocksANewAttempt(status) {
    return !CONCLUDED_ATTEMPT_STATUSES.includes(status);
}
/**
 * THE COMPLETE LIST OF WHAT A GENERAL RESULT CARRIES.
 *
 * READ CLAUDE.md SECTION 4 BEFORE ADDING AN ENTRY. Each is something a person
 * writes or states about work they did; not one is a measurement, and the
 * merge-gate test asserts the API returns nothing beyond it. Equipment used
 * and attachments are recorded alongside, as their own rows.
 */
export const GENERAL_RESULT_FIELDS = Object.freeze([
    'summaryAr',
    'summaryEn',
    'generalNotes',
    'executionStartedAt',
    'executionCompletedAt',
]);
/**
 * What stops a draft being submitted, as machine-readable codes.
 *
 * A summary in at least one language and both execution dates. That is a
 * completeness rule of the general WORKFLOW — a submission with no statement
 * of what was found, or no statement of when the work was done, is not
 * something a reviewer can act on — and it says nothing about any test.
 * Every gap is returned at once, so a person fixes them in one pass.
 */
export function generalResultGaps(content) {
    const gaps = [];
    const hasSummary = Boolean(content.summaryAr?.trim()) || Boolean(content.summaryEn?.trim());
    if (!hasSummary)
        gaps.push({ field: 'summary', code: 'required' });
    if (!content.executionStartedAt) {
        gaps.push({ field: 'executionStartedAt', code: 'required' });
    }
    if (!content.executionCompletedAt) {
        gaps.push({ field: 'executionCompletedAt', code: 'required' });
    }
    return gaps;
}
export const ATTEMPT_ATTACHMENT_PURPOSE = {
    /** A worksheet or spreadsheet. Attaching it does NOT mean the system read
     *  it, checked it, or used anything inside it. */
    WORKSHEET: 'worksheet',
    PHOTO: 'photo',
    OTHER: 'other',
};
export const ALL_ATTEMPT_ATTACHMENT_PURPOSES = Object.freeze(Object.values(ATTEMPT_ATTACHMENT_PURPOSE));
// ---------------------------------------------------------------------------
// The minimal equipment register (D85)
// ---------------------------------------------------------------------------
export const EQUIPMENT_STATUS = {
    IN_SERVICE: 'in_service',
    /** Out of use for a recorded reason. May return to service. */
    SUSPENDED: 'suspended',
    /** Permanently withdrawn. Final, and never usable, even by exception. */
    RETIRED: 'retired',
};
export const ALL_EQUIPMENT_STATUSES = Object.freeze(Object.values(EQUIPMENT_STATUS));
export function equipmentStatusRequiresReason(status) {
    return status !== EQUIPMENT_STATUS.IN_SERVICE;
}
/** Why a piece of equipment may not be used on a given date. */
export const EQUIPMENT_BLOCK_REASON = {
    RETIRED: 'retired',
    SUSPENDED: 'suspended',
    /** The calibration valid-until date is before the date of use. */
    CALIBRATION_EXPIRED: 'calibration_expired',
    /** The equipment requires calibration and none has been recorded. An empty
     *  date is not "calibrated forever" (`CLAUDE.md` section 8). */
    NO_CALIBRATION_RECORDED: 'no_calibration_recorded',
};
export const ALL_EQUIPMENT_BLOCK_REASONS = Object.freeze(Object.values(EQUIPMENT_BLOCK_REASON));
/**
 * The blocking rule, in one place.
 *
 * It reads ONLY the equipment's own state and its own calibration date against
 * the date of use. It deliberately knows nothing about the test, because which
 * apparatus a test requires is not known (limitation 26). The database applies
 * the same rule to the snapshot it takes, so a row claiming a blocked item was
 * usable cannot exist.
 *
 * `usedOn` and `calibrationValidUntil` are calendar dates (YYYY-MM-DD); the
 * valid-until date is the LAST day the calibration is valid.
 */
export function equipmentBlockReason(equipment, usedOn) {
    if (equipment.status === EQUIPMENT_STATUS.RETIRED) {
        return EQUIPMENT_BLOCK_REASON.RETIRED;
    }
    if (equipment.status === EQUIPMENT_STATUS.SUSPENDED) {
        return EQUIPMENT_BLOCK_REASON.SUSPENDED;
    }
    if (!equipment.calibrationRequired)
        return null;
    if (equipment.calibrationValidUntil === null) {
        return EQUIPMENT_BLOCK_REASON.NO_CALIBRATION_RECORDED;
    }
    return equipment.calibrationValidUntil < usedOn
        ? EQUIPMENT_BLOCK_REASON.CALIBRATION_EXPIRED
        : null;
}
/**
 * May a permissioned, reasoned exception be recorded for this block?
 *
 * A retired instrument is not an instrument the laboratory has, so there is
 * nothing to make an exception for.
 */
export function equipmentBlockMayBeOverridden(reason) {
    return reason !== EQUIPMENT_BLOCK_REASON.RETIRED;
}
// ---------------------------------------------------------------------------
// Files that belong to a record with its own permission
// ---------------------------------------------------------------------------
/**
 * Owner entity types whose files are served ONLY through the owning record's
 * route, which checks that record's permission (decision D92).
 *
 * The generic `/files` routes check `files.view` and nothing else, so a sample
 * photo served there would reach somebody who may not see the sample. For
 * these types the generic routes answer as though the file does not exist.
 *
 * `report_version` (Phase 10, D136): an issued report's stored PDF is served
 * only by `GET /reports/{id}/versions/{n}/file` behind `reports.view`, which
 * re-checks its hash and records `report.file_downloaded` — and by the public
 * verification download under its own rules. A generic route or signed link
 * must never be a second way to the controlled document.
 *
 * `equipment_calibration` (Phase 11, D156): a calibration certificate is
 * attached to a calibration record under `calibration.manage`, read through
 * the equipment file under `equipment.view`, kept permanently, and removed
 * only by marking. The generic routes treat it as nonexistent.
 */
export const DOMAIN_OWNED_FILE_ENTITY_TYPE = {
    SAMPLE: 'sample',
    TEST_ATTEMPT: 'test_attempt',
    REPORT_VERSION: 'report_version',
    EQUIPMENT_CALIBRATION: 'equipment_calibration',
};
export const ALL_DOMAIN_OWNED_FILE_ENTITY_TYPES = Object.freeze(Object.values(DOMAIN_OWNED_FILE_ENTITY_TYPE));
export function isDomainOwnedFileEntityType(value) {
    return ALL_DOMAIN_OWNED_FILE_ENTITY_TYPES.includes(value);
}
// ---------------------------------------------------------------------------
// Calendar dates in a laboratory's own timezone
// ---------------------------------------------------------------------------
/**
 * The calendar date (YYYY-MM-DD) of an instant, in the given IANA timezone.
 *
 * "Today" for a laboratory in Riyadh is not "today" in UTC for three hours of
 * every day, and a calibration that expires at the end of the day must not
 * block equipment three hours early.
 */
export function calendarDateInTimeZone(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const part = (type) => parts.find((candidate) => candidate.type === type)?.value ?? '';
    return `${part('year')}-${part('month')}-${part('day')}`;
}
// ---------------------------------------------------------------------------
// Wall-clock moments in a laboratory's own timezone
// ---------------------------------------------------------------------------
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
/** Minutes EAST of UTC that `timeZone` observes at `instantMs`. */
function zoneOffsetMinutes(instantMs, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date(instantMs));
    const part = (type) => Number(parts.find((candidate) => candidate.type === type)?.value ?? '0');
    const asIfUtc = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour') % 24, part('minute'), part('second'));
    return Math.round((asIfUtc - Math.floor(instantMs / 1000) * 1000) / 60_000);
}
const pad = (value, width = 2) => String(value).padStart(width, '0');
/**
 * The wall-clock minute (`YYYY-MM-DDTHH:mm`) an instant shows on a clock in
 * the given IANA timezone, or `null` for something that is not an instant.
 */
export function instantToZonedWallClock(instant, timeZone) {
    const ms = instant instanceof Date ? instant.getTime() : Date.parse(instant);
    if (Number.isNaN(ms))
        return null;
    const local = new Date(ms + zoneOffsetMinutes(ms, timeZone) * 60_000);
    return (`${pad(local.getUTCFullYear(), 4)}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}` +
        `T${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`);
}
/**
 * The instant a person means when they read `YYYY-MM-DDTHH:mm` off a clock in
 * the laboratory, as ISO 8601 WITH that zone's offset
 * (`2026-09-16T14:30:00+03:00`).
 *
 * The zone is the LABORATORY's (its settings), never the browser's: a
 * receptionist whose laptop is set to another zone still records the time on
 * the laboratory's wall. Returns `null` for a malformed or impossible value —
 * a day that does not exist, or a minute a daylight-saving change skips — so
 * nothing is ever silently moved to a neighbouring moment.
 */
export function zonedWallClockToInstant(wallClock, timeZone) {
    const match = WALL_CLOCK.exec(wallClock);
    if (!match)
        return null;
    const [year, month, day, hour, minute] = match.slice(1).map(Number);
    const asIfUtc = Date.UTC(year, month - 1, day, hour, minute);
    const check = new Date(asIfUtc);
    if (year < 1000 ||
        check.getUTCFullYear() !== year ||
        check.getUTCMonth() !== month - 1 ||
        check.getUTCDate() !== day ||
        check.getUTCHours() !== hour ||
        check.getUTCMinutes() !== minute) {
        return null;
    }
    // Two passes: the offset is a property of the instant, which is what is
    // being found. The second pass settles a guess that crossed a transition.
    let offset = zoneOffsetMinutes(asIfUtc, timeZone);
    offset = zoneOffsetMinutes(asIfUtc - offset * 60_000, timeZone);
    const instant = asIfUtc - offset * 60_000;
    if (instantToZonedWallClock(new Date(instant), timeZone) !== wallClock)
        return null;
    const sign = offset >= 0 ? '+' : '-';
    const absolute = Math.abs(offset);
    return `${wallClock}:00${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
}
//# sourceMappingURL=execution.js.map